import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import SEO from '@/components/SEO';
import { useAuth } from '@/context/auth-context';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { TurnstileBox } from '@/components/TurnstileBox';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showMfa, setShowMfa] = useState(false);
  const [mfaUserId, setMfaUserId] = useState('');
  const [isBackupMode, setIsBackupMode] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [showTurnstile, setShowTurnstile] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined);

  const { user, login, verifyMfa, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const defaultRedirect = '/studio';
  const redirect = searchParams.get('redirect') || defaultRedirect;
  const urlError = searchParams.get('error');

  React.useEffect(() => {
    if (urlError === 'account_suspended') {
      toast.error('Your account is suspended due to violation of the policy.', {
        description: 'Contact us at support@funcirclegames.com if you think this is a mistake.',
        duration: 10000,
      });
      // Remove query param to avoid multiple alerts
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('error');
      navigate({ search: newParams.toString() }, { replace: true });
    }
  }, [urlError, searchParams, navigate]);

  React.useEffect(() => {
    if (user) {
      navigate(redirect);
    }
  }, [user, navigate, redirect]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (showMfa) {
        const result = await verifyMfa(mfaUserId, mfaCode, isBackupMode, turnstileToken);
        
        if (result && result.requiresCaptcha) {
          setShowTurnstile(true);
          setLoading(false);
          toast.info('Security verification required', {
            description: 'Please complete the challenge to continue.'
          });
          return;
        }
        
        navigate(redirect);
      } else {
        const result = await login(email, password, turnstileToken);
        
        if (result && result.requiresCaptcha) {
          setShowTurnstile(true);
          setLoading(false);
          toast.info('Security verification required', {
            description: 'Please complete the challenge to continue.'
          });
          return;
        }

        if (result && result.mfa_required) {
          setShowMfa(true);
          setMfaUserId(result.user_id || '');
          setLoading(false);
          return;
        }
        navigate(redirect);
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
      // On generic failure, reset turnstile token to force re-verification if it was there
      setTurnstileToken(undefined);
    } finally {
      setLoading(false);
    }
  };

  if (showMfa) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-40 pb-20 bg-white relative overflow-hidden transition-colors duration-1000">
        <SEO title="MFA Verification" noindex />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(6,182,212,0.1)_0%,transparent_50%)]" />
        </div>

        <div className="max-w-[400px] w-full relative z-10">
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-5xl font-bold font-display text-cyan-950 tracking-tighter mb-3 uppercase">
              Two-Factor Auth
            </h1>
            <p className="text-zinc-600 font-medium italic text-sm">
              {isBackupMode ? 'Enter a backup recovery code.' : 'Enter the 6-digit code from your authenticator app.'}
            </p>
          </div>

          <div className="bg-white p-10 rounded-[2.5rem] border border-zinc-950/10 shadow-2xl shadow-black/5">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-500 text-[10px] font-black uppercase tracking-widest text-center">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-600 ml-1">
                  {isBackupMode ? 'Backup Code' : 'TOTP Token'}
                </label>
                <div className="relative group/input">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500 transition-colors group-focus-within/input:text-cyan-600" />
                  <input
                    type="text"
                    required
                    autoFocus
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    className="block w-full pl-12 pr-4 py-4 bg-zinc-50/50 border border-cyan-100 rounded-2xl text-cyan-950 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-cyan-600/5 focus:border-cyan-600 transition-all text-center tracking-[0.5em] text-lg font-mono uppercase"
                    placeholder={isBackupMode ? 'XXXX-XXXX' : '000000'}
                    maxLength={isBackupMode ? 9 : 6}
                  />
                </div>
              </div>

              <TurnstileBox 
                show={showTurnstile} 
                onVerify={(token) => setTurnstileToken(token)} 
              />

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-cyan-600 !text-white font-black uppercase tracking-[0.1em] rounded-2xl shadow-xl shadow-cyan-600/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-cyan-700 font-display"
              >
                {loading ? 'Verifying...' : (
                  <>
                    VERIFY CODE
                    <ArrowRight className="w-5 h-5 ml-1" />
                  </>
                )}
              </Button>

              <div className="pt-2 text-center">
                <button
                  type="button"
                  onClick={() => setIsBackupMode(!isBackupMode)}
                  className="text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-cyan-600 transition-colors"
                >
                  {isBackupMode ? 'Use Authenticator App' : 'Use a backup code'}
                </button>
              </div>
            </form>
          </div>
          
          <div className="mt-10 text-center">
            <button 
              onClick={() => setShowMfa(false)}
              className="text-zinc-500 text-sm font-medium hover:text-cyan-600"
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-40 pb-20 bg-white relative overflow-hidden transition-colors duration-1000">
      <SEO title="Log In" noindex />
      {/* Background accents */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(6,182,212,0.1)_0%,transparent_50%)]" />
      </div>

      <div className="max-w-[400px] w-full relative z-10">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-5xl font-bold font-display text-cyan-950 tracking-tighter mb-3 uppercase">
            Login
          </h1>
          <p className="text-zinc-600 font-medium italic text-sm">Please enter your details to login.</p>
        </div>

        <div className="bg-white p-10 rounded-[2.5rem] border border-zinc-950/10 shadow-2xl shadow-black/5">
          <div className="mb-8">
            <button
              onClick={loginWithGoogle}
              className="w-full flex items-center justify-center gap-3 px-4 h-14 border border-zinc-950/10 rounded-2xl bg-white text-zinc-700 hover:bg-zinc-50 hover:border-cyan-600/30 transition-all font-bold uppercase text-[11px] tracking-[0.1em] shadow-sm hover:shadow-md active:scale-[0.98] relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-linear-to-r from-cyan-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="relative z-10">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-1 .67-2.28 1.07-3.71 1.07-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.11c-.22-.66-.35-1.36-.35-2.11s.13-1.45.35-2.11V7.05H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.95l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84c.87-2.6 3.3-4.51 6.16-4.51z" fill="#EA4335"/>
              </svg>
              <span className="text-cyan-950 relative z-10">Continue with Google</span>
            </button>
            <p className="mt-3 text-[9px] text-zinc-400 text-center font-medium uppercase tracking-tight px-4">
              By logging in, you agree to our <Link to="/terms-of-service" className="underline">Terms</Link> & <Link to="/privacy-policy" className="underline">Privacy Policy</Link>
            </p>
          </div>

          <div className="relative mb-8 text-center">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-zinc-100"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-white text-[10px] font-black uppercase tracking-widest text-zinc-400">
                Or login with email
              </span>
            </div>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-500 text-[10px] font-black uppercase tracking-widest text-center">
                {error}
              </div>
            )}

            <div className="space-y-5">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-600 ml-1">Email</label>
                <div className="relative group/input">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500 transition-colors group-focus-within/input:text-cyan-600" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full pl-12 pr-4 py-4 bg-zinc-50/50 border border-cyan-100 rounded-2xl text-cyan-950 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-cyan-600/5 focus:border-cyan-600 transition-all"
                    placeholder="name@example.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center px-1">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-600">Password</label>
                  <Link to="/forgot-password" title="Forgot password?" className="text-[9px] font-black uppercase tracking-widest text-zinc-600 hover:text-cyan-600 transition-colors">
                    Forgot?
                  </Link>
                </div>
                <div className="relative group/input">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500 transition-colors group-focus-within/input:text-cyan-600" />
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-12 pr-4 py-4 bg-zinc-50/50 border border-cyan-100 rounded-2xl text-cyan-950 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-cyan-600/5 focus:border-cyan-600 transition-all"
                    placeholder="********"
                  />
                </div>
              </div>
            </div>

            <TurnstileBox 
              show={showTurnstile} 
              onVerify={(token) => setTurnstileToken(token)} 
            />

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-cyan-600 !text-white font-black uppercase tracking-[0.1em] rounded-2xl shadow-xl shadow-cyan-600/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-cyan-700 font-display"
            >
              {loading ? 'Logging in...' : (
                <>
                  LOGIN
                  <ArrowRight className="w-5 h-5 ml-1" />
                </>
              )}
            </Button>
          </form>
        </div>

        <div className="mt-10 text-center">
          <p className="text-zinc-500 text-sm font-medium">
            Don't have an account?{' '}
            <Link to="/register" className="font-bold text-cyan-600 hover:underline transition-all">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
